
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { BoardCategory } from '@/types/board';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { LinkShareForm } from '@/components/board/LinkShareForm';
import CategoryTabs from '@/components/community/CategoryTabs';
import PostsTable from '@/components/community/PostsTable';
import BoardHeader from '@/components/community/BoardHeader';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { getPosts } from '@/services/postService';
import { useQuery } from '@tanstack/react-query';

const BulletinBoard = () => {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const categories: BoardCategory[] = [
    '자유게시판',
    '법률정보',
    '구인구직',
    '사고팔고',
    '부동산',
    'Weee Link 공유'
  ];
  
  const [activeCategory, setActiveCategory] = useState<BoardCategory>(
    (categoryParam as BoardCategory) || '자유게시판'
  );
  
  const [showLinkForm, setShowLinkForm] = useState(false);
  
  const { data: posts = [], isLoading, error } = useQuery({
    queryKey: ['posts', activeCategory],
    queryFn: () => getPosts(activeCategory),
  });
  
  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "게시글을 불러오는데 실패했습니다",
        description: "잠시 후 다시 시도해주세요."
      });
    }
  }, [error, toast]);
  
  // Hide link form when category changes
  useEffect(() => {
    setShowLinkForm(false);
  }, [activeCategory]);
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleWritePost = () => {
    if (activeCategory === 'Weee Link 공유') {
      setShowLinkForm(true);
    } else {
      navigate(`/board/write?category=${encodeURIComponent(activeCategory)}`);
    }
  };

  // Helper function to get author name safely
  const getAuthorName = (post) => {
    // Fallback to author_name field if it exists
    if (post.author_name) {
      return post.author_name;
    }
    
    // Final fallback
    return 'Anonymous';
  };
  
  // Handle post click according to category
  const handlePostClick = (post, e) => {
    if (post.category === 'Weee Link 공유') {
      e.preventDefault();
      window.location.href = post.content;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto py-8 px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">게시판</h1>
          <p className="text-muted-foreground mt-2">베가스킴 커뮤니티와 소통해보세요.</p>
        </div>
        
        <div className="flex justify-between items-center mb-6">
          <CategoryTabs 
            categories={categories} 
            activeCategory={activeCategory} 
            setActiveCategory={setActiveCategory}
          >
            <BoardHeader 
              activeCategory={activeCategory} 
              user={user} 
              handleWritePost={handleWritePost} 
            />
            
            {showLinkForm && activeCategory === 'Weee Link 공유' && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium">링크 공유하기</h3>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => setShowLinkForm(false)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">닫기</span>
                  </Button>
                </div>
                <LinkShareForm 
                  user={user} 
                  inline={true} 
                  onCancel={() => setShowLinkForm(false)} 
                />
              </div>
            )}
            
            <Card>
              <CardContent className="p-0">
                <PostsTable 
                  posts={posts} 
                  isLoading={isLoading} 
                  formatDate={formatDate} 
                  getAuthorName={getAuthorName}
                  handlePostClick={handlePostClick}
                />
              </CardContent>
            </Card>
            
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious href="#" />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink href="#" isActive>1</PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext href="#" />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </CategoryTabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BulletinBoard;
